<section class="dash-content">

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <div class="dash-header">
    <h1 class="page-title">{{ 'dashboard.title' | translate }}</h1>
    <p class="page-subtitle">{{ 'dashboard.subtitle' | translate }}</p>
  </div>

  <!-- ── Tabs — group the seven sections by concern so the admin reads one
       story at a time instead of scrolling the whole page. ── -->
  <div class="dash-tabs" role="tablist">
    <button type="button" role="tab" class="dash-tab" [class.dash-tab-active]="activeTab === 'overview'"
            [attr.aria-selected]="activeTab === 'overview'" (click)="setTab('overview')">
      <i class="pi pi-th-large"></i>{{ 'dashboard.tabs.overview' | translate }}
    </button>
    <button type="button" role="tab" class="dash-tab" [class.dash-tab-active]="activeTab === 'revenue'"
            [attr.aria-selected]="activeTab === 'revenue'" (click)="setTab('revenue')">
      <i class="pi pi-chart-line"></i>{{ 'dashboard.tabs.revenue' | translate }}
    </button>
    <button type="button" role="tab" class="dash-tab" [class.dash-tab-active]="activeTab === 'catalog'"
            [attr.aria-selected]="activeTab === 'catalog'" (click)="setTab('catalog')">
      <i class="pi pi-box"></i>{{ 'dashboard.tabs.catalog' | translate }}
    </button>
  </div>

  <!-- ════════════════════════ TAB 1 — OVERVIEW ═══════════════════════════ -->
  <div [class.dash-tab-hidden]="activeTab !== 'overview'">

    <!-- ── All business accounts — the whole population as one bar: the buyer side
         (restaurants, hotels, and the owners of branch ones) first, then suppliers.
         Every count includes not-started registrations and equals the list it opens.
         All-time; the Revenue tab's date filter never applies here. ── -->
    <div class="dash-panel surface-card dash-users">
      <div class="dash-users-head">
        <span class="dash-icon dash-accent-brand"><i class="pi pi-users"></i></span>
        <div>
          <span class="dash-kpi-label">{{ 'dashboard.allBusinessAccounts' | translate }}</span>
          <div class="dash-users-total">{{ accountsCount }}</div>
        </div>
        <p class="dash-users-note">{{ 'dashboard.allBusinessAccountsNote' | translate }}</p>
      </div>

      <!-- The legend below carries the same numbers as text, so the bar is decorative. -->
      <div class="dash-pop-bar" aria-hidden="true">
        <ng-container *ngFor="let s of populationSegments">
          <span *ngIf="s.count > 0"
                class="dash-pop-seg dash-accent-{{ s.accent }}"
                [style.flex-grow]="s.count"
                [title]="(s.labelKey | translate) + ': ' + s.count"></span>
        </ng-container>
      </div>

      <div class="dash-pop-legend">
        <!-- Buyer side: buyers and the owners who hold branch restaurants/hotels. -->
        <div class="dash-pop-group dash-pop-group--buyer-side">
          <div class="dash-pop-part">
            <div class="dash-pop-main">
              <span class="dash-pop-name">{{ 'dashboard.buyers' | translate }}</span>
              <span class="dash-pop-count">{{ buyersCount }}</span>
            </div>
            <div class="dash-pop-subs">
              <a class="dash-pop-sub dash-link" routerLink="/buyers" [queryParams]="restaurantLinks.total"
                 [title]="'dashboard.showInBuyers' | translate">
                <span class="dash-swatch dash-accent-brand"></span>{{ 'dashboard.restaurants' | translate }}
                <span class="dash-strong">{{ restaurants.total }}</span>
              </a>
              <a class="dash-pop-sub dash-link" routerLink="/buyers" [queryParams]="hotelLinks.total"
                 [title]="'dashboard.showInBuyers' | translate">
                <span class="dash-swatch dash-accent-blue"></span>{{ 'dashboard.hotels' | translate }}
                <span class="dash-strong">{{ hotels.total }}</span>
              </a>
            </div>
          </div>
          <div class="dash-pop-part">
            <a class="dash-pop-main dash-link" routerLink="/buyers" [queryParams]="ownersLink"
               [title]="'dashboard.showInBuyers' | translate">
              <span class="dash-pop-name"><span class="dash-swatch dash-accent-orange"></span>{{ 'dashboard.businessOwners' | translate }}</span>
              <span class="dash-pop-count">{{ owners.total }}</span>
            </a>
            <div class="dash-pop-subs">
              <span class="dash-pop-sub">{{ 'dashboard.restaurantOwners' | translate }} <span class="dash-strong">{{ owners.restaurant }}</span></span>
              <span class="dash-pop-sub">{{ 'dashboard.hotelOwners' | translate }} <span class="dash-strong">{{ owners.hotel }}</span></span>
            </div>
          </div>
        </div>
        <div class="dash-pop-group">
          <a class="dash-pop-main dash-link" routerLink="/suppliers" [title]="'dashboard.showSuppliers' | translate">
            <span class="dash-pop-name"><span class="dash-swatch dash-accent-purple"></span>{{ 'dashboard.suppliers' | translate }}</span>
            <span class="dash-pop-count">{{ suppliers.total }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- ── Buyers — restaurants and hotels, plus the business owners that hold the
         Branch ones. Owners sit here, on the buyer side, not beside suppliers. ── -->
    <h2 class="dash-section-title">{{ 'dashboard.buyers' | translate }}</h2>
    <div class="grid dash-grid">
      <ng-container *ngTemplateOutlet="buyerCard; context: { $implicit: restaurants, links: restaurantLinks, labelKey: 'dashboard.totalRestaurants', icon: 'pi-shop', accent: 'brand' }"></ng-container>
      <ng-container *ngTemplateOutlet="buyerCard; context: { $implicit: hotels, links: hotelLinks, labelKey: 'dashboard.totalHotels', icon: 'pi-building', accent: 'blue' }"></ng-container>

      <div class="col-12 xl:col-4">
        <div class="dash-panel surface-card h-full">
          <div class="dash-owners-head dash-accent-orange">
            <span class="dash-icon"><i class="pi pi-briefcase"></i></span>
            <div>
              <span class="dash-kpi-label">{{ 'dashboard.businessOwners' | translate }}</span>
              <a class="dash-kpi-value dash-kpi-value-lg dash-link" routerLink="/buyers" [queryParams]="ownersLink"
                 [title]="'dashboard.showInBuyers' | translate">{{ owners.total }}</a>
            </div>
          </div>
          <div class="dash-divider"></div>
          <ul class="dash-stat-list">
            <li>
              <span class="dash-stat-key">{{ 'dashboard.restaurantOwners' | translate }}</span>
              <span class="dash-stat-val">{{ owners.restaurant }}</span>
            </li>
            <li>
              <span class="dash-stat-key">{{ 'dashboard.hotelOwners' | translate }}</span>
              <span class="dash-stat-val">{{ owners.hotel }}</span>
            </li>
          </ul>
          <p class="dash-card-caption">{{ 'dashboard.ownersCaption' | translate }}</p>
        </div>
      </div>
    </div>

    <!-- ── Suppliers — one card, the same width as a Buyers card above rather than
         stretched across the page. ── -->
    <h2 class="dash-section-title">{{ 'dashboard.suppliers' | translate }}</h2>
    <div class="grid dash-grid">
      <div class="col-12 lg:col-6 xl:col-4">
        <div class="dash-panel surface-card h-full dash-supplier-card">
          <div class="dash-owners-head dash-accent-purple">
            <span class="dash-icon"><i class="pi pi-truck"></i></span>
            <div>
              <span class="dash-kpi-label">{{ 'dashboard.totalSuppliers' | translate }}</span>
              <div class="dash-stat-end">
                <a class="dash-kpi-value dash-kpi-value-lg dash-link" routerLink="/suppliers"
                   [title]="'dashboard.showSuppliers' | translate">{{ suppliers.total }}</a>
                <a *ngIf="suppliers.notStarted > 0" class="dash-pill-muted" routerLink="/suppliers" [queryParams]="suppliersNotStartedLink"
                   [title]="'dashboard.notStartedHint' | translate">{{ 'dashboard.notStartedCount' | translate: { count: suppliers.notStarted } }}</a>
              </div>
            </div>
          </div>
          <ul class="dash-stat-inline">
            <li>
              <span class="dash-stat-key">{{ 'dashboard.active' | translate }}</span>
              <span class="dash-stat-val">{{ suppliers.active }}</span>
            </li>
            <li>
              <span class="dash-stat-key">{{ 'dashboard.incomplete' | translate }}</span>
              <span class="dash-stat-val" [class.dash-val-warn]="suppliers.incomplete > 0">{{ suppliers.incomplete }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- ── Operational Snapshot ── -->
    <h2 class="dash-section-title">{{ 'dashboard.operationalSnapshot' | translate }}</h2>
    <div class="grid dash-grid">

      <!-- Orders -->
      <div class="col-12 md:col-6">
        <div class="dash-kpi dash-accent-brand surface-card">
          <div class="dash-kpi-head">
            <span class="dash-icon"><i class="pi pi-box"></i></span>
            <span class="dash-kpi-label">{{ 'dashboard.orders' | translate }}</span>
          </div>
          <div class="dash-kpi-value">{{ dashboardResponseModule?.subOrdersCount ?? 0 }}</div>
          <div class="dash-kpi-foot">
            <span class="dash-kpi-sub">{{ 'dashboard.total' | translate }}</span>
            <p-tag [value]="(dashboardResponseModule?.approvedOrdersCount ?? 0) + ' ' + ('dashboard.approved' | translate)" severity="success" styleClass="dash-tag"></p-tag>
          </div>
        </div>
      </div>

      <!-- Request Orders -->
      <div class="col-12 md:col-6">
        <div class="dash-kpi dash-accent-brand surface-card">
          <div class="dash-kpi-head">
            <span class="dash-icon"><i class="pi pi-briefcase"></i></span>
            <span class="dash-kpi-label">{{ 'dashboard.requestOrders' | translate }}</span>
          </div>
          <div class="dash-kpi-value">{{ dashboardResponseModule?.requestOrdersCount ?? 0 }}</div>
          <div class="dash-kpi-foot">
            <span class="dash-kpi-sub">{{ 'dashboard.total' | translate }}</span>
            <p-tag [value]="(dashboardResponseModule?.requestOffersCount ?? 0) + ' ' + ('dashboard.requestOffer' | translate)" severity="info" styleClass="dash-tag"></p-tag>
          </div>
        </div>
      </div>

    </div>


    <!-- One Restaurants/Hotels card. Individual + Branch = total; each already includes
         its not-started accounts, flagged by a badge (hidden at 0) beside the number. -->
    <ng-template #buyerCard let-c let-links="links" let-labelKey="labelKey" let-icon="icon" let-accent="accent">
      <div class="col-12 lg:col-6 xl:col-4">
        <div class="dash-panel surface-card h-full">
          <div class="dash-owners-head dash-accent-{{ accent }}">
            <span class="dash-icon"><i class="pi {{ icon }}"></i></span>
            <div>
              <span class="dash-kpi-label">{{ labelKey | translate }}</span>
              <a class="dash-kpi-value dash-kpi-value-lg dash-link" routerLink="/buyers" [queryParams]="links.total"
                 [title]="'dashboard.showInBuyers' | translate">{{ c.total }}</a>
            </div>
          </div>

          <div class="dash-divider"></div>

          <ul class="dash-stat-list">
            <li>
              <span class="dash-stat-key">{{ 'dashboard.individual' | translate }}</span>
              <span class="dash-stat-end">
                <a *ngIf="c.individualIncomplete > 0" class="dash-pill-warn" routerLink="/buyers" [queryParams]="links.individualIncomplete"
                   [title]="'dashboard.showInBuyers' | translate">{{ 'dashboard.incompleteCount' | translate: { count: c.individualIncomplete } }}</a>
                <a *ngIf="c.individualNotStarted > 0" class="dash-pill-muted" routerLink="/buyers" [queryParams]="links.individualNotStarted"
                   [title]="'dashboard.notStartedHint' | translate">{{ 'dashboard.notStartedCount' | translate: { count: c.individualNotStarted } }}</a>
                <a class="dash-stat-val dash-link" routerLink="/buyers" [queryParams]="links.individual"
                   [title]="'dashboard.showInBuyers' | translate">{{ c.individual }}</a>
              </span>
            </li>
            <li>
              <span class="dash-stat-key">{{ 'dashboard.branch' | translate }}</span>
              <span class="dash-stat-end">
                <a *ngIf="c.branchIncomplete > 0" class="dash-pill-warn" routerLink="/buyers" [queryParams]="links.branchIncomplete"
                   [title]="'dashboard.showInBuyers' | translate">{{ 'dashboard.incompleteCount' | translate: { count: c.branchIncomplete } }}</a>
                <a *ngIf="c.branchNotStarted > 0" class="dash-pill-muted" routerLink="/buyers" [queryParams]="links.branchNotStarted"
                   [title]="'dashboard.notStartedHint' | translate">{{ 'dashboard.notStartedCount' | translate: { count: c.branchNotStarted } }}</a>
                <a class="dash-stat-val dash-link" routerLink="/buyers" [queryParams]="links.branch"
                   [title]="'dashboard.showInBuyers' | translate">{{ c.branch }}</a>
              </span>
            </li>
            <li>
              <span class="dash-stat-key">{{ 'dashboard.active' | translate }}</span>
              <span class="dash-stat-val">{{ c.active }}</span>
            </li>
          </ul>
        </div>
      </div>
    </ng-template>
  </div>

  <!-- ════════════════════════ TAB 2 — REVENUE ════════════════════════════ -->
  <div [class.dash-tab-hidden]="activeTab !== 'revenue'">

    <!-- ── All-Time Metrics ── -->
    <h2 class="dash-section-title">{{ 'dashboard.allTimeMetrics' | translate }}</h2>
    <div class="grid dash-grid">
      <div class="col-12 md:col-6">
        <div class="dash-kpi dash-kpi-row dash-accent-brand surface-card">
          <span class="dash-icon"><i class="pi pi-money-bill"></i></span>
          <div class="flex-1">
            <span class="dash-kpi-label">{{ 'dashboard.avgOrderValue' | translate }}</span>
            <div class="dash-kpi-value dash-kpi-value-sm">{{ formatCurrency(averageOrderValue) }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 md:col-6">
        <div class="dash-kpi dash-kpi-row dash-accent-brand surface-card">
          <span class="dash-icon"><i class="pi pi-percentage"></i></span>
          <div class="flex-1">
            <span class="dash-kpi-label">
              {{ 'dashboard.commission' | translate }}
              <i class="pi pi-info-circle dash-info"
                 [pTooltip]="'dashboard.commissionHint' | translate"
                 tooltipPosition="top"
                 [tooltipStyleClass]="'max-w-20rem white-space-normal line-height-3'"></i>
            </span>
            <div class="dash-kpi-value dash-kpi-value-sm">{{ formatCurrency(commissionRevenue) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Revenue Performance ── -->
    <h2 class="dash-section-title">{{ 'dashboard.revenuePerformance' | translate }}</h2>

    <!-- Date Filtered Revenue -->
    <div class="dash-panel surface-card">
      <div class="dash-panel-head">
        <h3 class="dash-panel-title">{{ 'dashboard.revenueDateFiltered' | translate }}</h3>
        <div class="dash-date-filter">
          <p-calendar [(ngModel)]="startDate"
                      [showIcon]="true"
                      [maxDate]="endDate || today"
                      appendTo="body"
                      dateFormat="yy-mm-dd"
                      [placeholder]="'dashboard.startDate' | translate">
          </p-calendar>
          <span class="dash-date-sep">{{ 'dashboard.dateRangeSeparator' | translate }}</span>
          <p-calendar [(ngModel)]="endDate"
                      [showIcon]="true"
                      [minDate]="startDate"
                      [maxDate]="today"
                      appendTo="body"
                      dateFormat="yy-mm-dd"
                      [placeholder]="'dashboard.endDate' | translate">
          </p-calendar>
          <p-button [label]="'dashboard.apply' | translate"
                    icon="pi pi-check"
                    iconPos="right"
                    (onClick)="onDateFilterChange()">
          </p-button>
        </div>
      </div>

      <div class="grid dash-grid">
        <div class="col-12 md:col-6">
          <div class="dash-metric-tile dash-accent-brand">
            <span class="dash-icon"><i class="pi pi-wallet"></i></span>
            <div>
              <span class="dash-kpi-label">{{ 'dashboard.totalRevenue' | translate }}</span>
              <div class="dash-kpi-value dash-kpi-value-sm">{{ formatCurrency(totalRevenue) }}</div>
            </div>
          </div>
        </div>

        <div class="col-12 md:col-6">
          <div class="dash-metric-tile"
               [class.dash-accent-up]="revenueGrowthPercent >= 0"
               [class.dash-accent-down]="revenueGrowthPercent < 0">
            <span class="dash-icon">
              <i [class]="revenueGrowthPercent >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"></i>
            </span>
            <div class="flex-1">
              <span class="dash-kpi-label">
                {{ 'dashboard.growthPercent' | translate }}
                <i class="pi pi-info-circle dash-info"
                   [pTooltip]="'dashboard.growthHint' | translate"
                   tooltipPosition="top"
                   [tooltipStyleClass]="'max-w-20rem white-space-normal line-height-3'"></i>
              </span>
              <div class="dash-growth-row">
                <span class="dash-kpi-value dash-kpi-value-sm" [class.dash-up]="revenueGrowthPercent >= 0" [class.dash-down]="revenueGrowthPercent < 0">
                  {{ revenueGrowthPercent >= 0 ? '+' : '' }}{{ revenueGrowthPercent }}%
                </span>
                <p-tag
                  [severity]="revenueGrowthPercent >= 0 ? 'success' : 'danger'"
                  [value]="revenueGrowthPercent >= 0 ? ('dashboard.growthTag' | translate) : ('dashboard.declineTag' | translate)"
                  styleClass="dash-tag">
                </p-tag>
              </div>
              <span class="dash-kpi-sub-line">{{ 'dashboard.vsPreviousPeriod' | translate }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Revenue Trends -->
    <div class="dash-panel surface-card">
      <div class="dash-panel-head">
        <h3 class="dash-panel-title">{{ 'dashboard.revenueTrends' | translate }}</h3>
        <p-selectButton
          [options]="revenueTrendOptions"
          [(ngModel)]="revenueTrendPeriod"
          (onChange)="onRevenueTrendPeriodChange()">
        </p-selectButton>
      </div>
      <div class="chart-container" *ngIf="hasRevenueTrendData; else noRevenueTrendData">
        <p-chart type="line"
                 [data]="revenueTrendChartData"
                 [options]="revenueTrendChartOptions">
        </p-chart>
      </div>
      <ng-template #noRevenueTrendData>
        <div class="chart-empty-state">
          <div class="chart-empty-icon-wrap">
            <i class="pi pi-chart-line chart-empty-icon"></i>
          </div>
          <span class="chart-empty-title">{{ 'dashboard.noChartData' | translate }}</span>
          <span class="chart-empty-hint">{{ 'dashboard.noRevenueTrendHint' | translate }}</span>
        </div>
      </ng-template>
    </div>

    <!-- ── Revenue Breakdown ── -->
    <h2 class="dash-section-title">{{ 'dashboard.revenueBreakdown' | translate }}</h2>
    <div class="grid dash-grid">

      <div class="col-12 lg:col-6">
        <div class="dash-panel surface-card h-full">
          <div class="dash-panel-head">
            <h3 class="dash-panel-title">{{ 'dashboard.top10RevenueByCategory' | translate }}</h3>
            <p-tag severity="secondary"
                   [value]="formatCurrency(categoryTotal)"
                   styleClass="dash-tag">
            </p-tag>
          </div>
          <div class="chart-container chart-container-ranked" *ngIf="hasCategoryData; else noCategoryData">
            <p-chart type="bar"
                     [data]="revenueByCategoryChartData"
                     [options]="revenueByCategoryChartOptions">
            </p-chart>
          </div>
          <ng-template #noCategoryData>
            <div class="chart-empty-state">
              <div class="chart-empty-icon-wrap">
                <i class="pi pi-chart-bar chart-empty-icon"></i>
              </div>
              <span class="chart-empty-title">{{ 'dashboard.noChartData' | translate }}</span>
              <span class="chart-empty-hint">{{ 'dashboard.noCategoryHint' | translate }}</span>
            </div>
          </ng-template>
        </div>
      </div>

      <div class="col-12 lg:col-6">
        <div class="dash-panel surface-card h-full">
          <div class="dash-panel-head">
            <h3 class="dash-panel-title">{{ 'dashboard.top10RevenueBySupplier' | translate }}</h3>
            <p-tag severity="secondary"
                   [value]="formatCurrency(supplierTotal)"
                   styleClass="dash-tag">
            </p-tag>
          </div>
          <div class="chart-container chart-container-ranked" *ngIf="hasSupplierData; else noSupplierData">
            <p-chart type="bar"
                     [data]="revenueBySupplierChartData"
                     [options]="revenueBySupplierChartOptions">
            </p-chart>
          </div>
          <ng-template #noSupplierData>
            <div class="chart-empty-state">
              <div class="chart-empty-icon-wrap">
                <i class="pi pi-chart-bar chart-empty-icon"></i>
              </div>
              <span class="chart-empty-title">{{ 'dashboard.noChartData' | translate }}</span>
              <span class="chart-empty-hint">{{ 'dashboard.noSupplierHint' | translate }}</span>
            </div>
          </ng-template>
        </div>
      </div>

    </div>
  </div>

  <!-- ════════════════════ TAB 3 — PRODUCTS & ORDERS ══════════════════════ -->
  <div [class.dash-tab-hidden]="activeTab !== 'catalog'">

    <!-- ── Top Performing Products ── -->
    <h2 class="dash-section-title">{{ 'dashboard.topPerformingProducts' | translate }}</h2>
    <div class="dash-panel surface-card">
      <div class="dash-panel-head">
        <h3 class="dash-panel-title">{{ 'dashboard.topPerformingProducts' | translate }}</h3>
        <p-selectButton
          [options]="topProductsViewOptions"
          [(ngModel)]="topProductsView"
          (onChange)="onTopProductsViewChange()">
        </p-selectButton>
      </div>

      <div class="dashboard-table-container">
        <p-table appTableLoading
          #dt
          [value]="topProducts"
          responsiveLayout="scroll"
          [rows]="pageSize"
          [first]="first"
          [totalRecords]="topProducts.length"
          [paginator]="true"
          (onPage)="onPageChange($event)"
          styleClass="p-datatable-sm dashboard-custom-table">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'dashboard.product' | translate }}</th>
              <th>{{ 'dashboard.quantitySold' | translate }}</th>
              <th>{{ 'dashboard.ordersCol' | translate }}</th>
              <th class="text-right">{{ 'dashboard.revenueCol' | translate }}</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-p>
            <tr>
              <td>
                <div class="flex align-items-center gap-2">
                  <div class="w-3rem h-3rem flex align-items-center justify-content-center border-round shadow-1 shrink-0 product-avatar overflow-hidden">
                    <img *ngIf="p.imageUrl" [src]="p.imageUrl" [alt]="p.productName" class="w-full h-full" style="object-fit: cover; border-radius: inherit;" />
                    <i *ngIf="!p.imageUrl" class="pi pi-box text-sm" style="color: var(--green)"></i>
                  </div>
                  <span class="font-medium text-900">{{ p.productName }}</span>
                </div>
              </td>
              <td>{{ p.quantitySold }}</td>
              <td>{{ p.paidOrderCount }}</td>
              <td class="text-right font-bold text-900">{{ formatCurrency(p.revenue) }}</td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="text-center text-500 py-4">{{ 'dashboard.noProductsFound' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>

        <!-- Custom Pagination -->
        <div class="pagination-container">
          <div class="pagination-info">
            {{ 'pagination.showingRange' | translate:{ from: getShowingFrom(), to: getShowingTo(), total: getTotalFiltered(dt) } }}
          </div>
          <div class="pagination-controls">
            <div class="pagination-page-select">
              <label>{{ 'pagination.page' | translate }}</label>
              <select [ngModel]="getCurrentPage()" (ngModelChange)="goToPage($event, dt)">
                <option *ngFor="let page of getPageNumbers(dt)" [ngValue]="page">{{ page }}</option>
              </select>
            </div>
            <button class="pagination-btn" [disabled]="isFirstPage()" (click)="previousPage(dt)">
              {{ 'pagination.previous' | translate }}
            </button>
            <span class="pagination-page">{{ getCurrentPage() }}</span>
            <button class="pagination-btn" [disabled]="isLastPage(dt)" (click)="nextPage(dt)">
              {{ 'pagination.next' | translate }}
            </button>
          </div>
          <div class="pagination-size">
            <label>{{ 'pagination.pageSize' | translate }}</label>
            <select [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event, dt)">
              <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Order & Product Overview ── -->
    <h2 class="dash-section-title">{{ 'dashboard.orderProductOverview' | translate }}</h2>
    <div class="grid dash-grid">

      <!-- Orders Doughnut -->
      <div class="col-12 lg:col-4">
        <div class="dash-panel surface-card h-full">
          <div class="dash-panel-head">
            <h3 class="dash-panel-title">{{ 'dashboard.orders' | translate }}</h3>
          </div>
          <div class="dash-panel-sub-line">
            {{ 'dashboard.totalOrders' | translate }}: <span class="dash-strong">{{ dashboardResponseModule?.subOrdersCount ?? 0 }}</span>
          </div>
          <div class="chart-container" *ngIf="hasOrdersData; else noOrdersData">
            <p-chart type="doughnut" [data]="orderChartData" [options]="orderChartOptions"></p-chart>
          </div>
          <ng-template #noOrdersData>
            <div class="chart-empty-state">
              <div class="chart-empty-icon-wrap">
                <i class="pi pi-inbox chart-empty-icon"></i>
              </div>
              <span class="chart-empty-title">{{ 'dashboard.noChartData' | translate }}</span>
              <span class="chart-empty-hint">{{ 'dashboard.noOrdersHint' | translate }}</span>
            </div>
          </ng-template>
        </div>
      </div>

      <!-- Products Bar Chart -->
      <div class="col-12 lg:col-8">
        <div class="dash-panel surface-card h-full">
          <div class="dash-panel-head">
            <h3 class="dash-panel-title">{{ 'dashboard.productsOverview' | translate }}</h3>
          </div>
          <div class="chart-container" *ngIf="hasProductsData; else noProductsData">
            <p-chart type="bar" [data]="ProductsChartData" [options]="ProductsChartOptions"></p-chart>
          </div>
          <ng-template #noProductsData>
            <div class="chart-empty-state">
              <div class="chart-empty-icon-wrap">
                <i class="pi pi-chart-bar chart-empty-icon"></i>
              </div>
              <span class="chart-empty-title">{{ 'dashboard.noChartData' | translate }}</span>
              <span class="chart-empty-hint">{{ 'dashboard.noProductsHint' | translate }}</span>
            </div>
          </ng-template>
        </div>
      </div>

    </div>
  </div>

</section>
